import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { EntityMatch, registerLexicalTextEntity } from '@lexical/text';
import { mergeRegister } from '@lexical/utils';
import {
  $applyNodeReplacement,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical';
import { useEffect } from 'react';

export const STRING_LITERAL_CLASS = 'formula-string-literal';

/**
 * Pure syntax highlighting for double-quoted string literals: a text entity
 * (Hashtag-style), so it stays ordinary editable text — no chip semantics —
 * and converts back to plain text the moment the quotes no longer match.
 */
export class StringLiteralNode extends TextNode {
  static getType(): string {
    return 'formula-string';
  }

  static clone(node: StringLiteralNode): StringLiteralNode {
    return new StringLiteralNode(node.__text, node.__key);
  }

  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = STRING_LITERAL_CLASS;
    return dom;
  }

  static importJSON(serializedNode: SerializedTextNode): StringLiteralNode {
    return $createStringLiteralNode(serializedNode.text).updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedTextNode {
    return { ...super.exportJSON(), type: StringLiteralNode.getType() };
  }

  /**
   * False, against `registerLexicalTextEntity`'s advice. Its two adjacency
   * rules — never start an entity where another one ends, and unwrap a pair
   * that ends up side by side — assume hashtag-like entities that plain text
   * always separates. Highlighted formula tokens legitimately touch ("IF(AND(",
   * "ABS(1"), and under those rules whichever of the pair the transforms
   * reached second lost its colour.
   */
  isTextEntity(): false {
    return false;
  }
}

export function $createStringLiteralNode(text: string): StringLiteralNode {
  return $applyNodeReplacement(new StringLiteralNode(text));
}

export function $isStringLiteralNode(node: LexicalNode | null | undefined): node is StringLiteralNode {
  return node instanceof StringLiteralNode;
}

/** First complete "..." literal in the text; "" is Excel's escaped quote. */
export function getStringMatch(text: string): EntityMatch | null {
  const match = /"(?:""|[^"])*"/.exec(text);
  return match ? { start: match.index, end: match.index + match[0].length } : null;
}

/**
 * Whether the caret position (given the text before it) sits inside an open
 * string literal. Unescaped quotes flip the state; Excel's "" escape counts
 * as two and cancels out, so simple parity works.
 */
export function isInsideStringLiteral(textBeforeCaret: string): boolean {
  let quotes = 0;
  for (const ch of textBeforeCaret) {
    if (ch === '"') {
      quotes++;
    }
  }
  return quotes % 2 === 1;
}

export function StringLiteralPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return mergeRegister(
      ...registerLexicalTextEntity(editor, getStringMatch, StringLiteralNode, (textNode) =>
        $createStringLiteralNode(textNode.getTextContent())
      )
    );
  }, [editor]);
  return null;
}
